/* eslint-disable i18n-scan/no-inline-content */
import {
  BodyText,
  CaptionText,
  Card,
  Col,
  Divider,
  HeadingText,
  Row,
  Tooltip,
} from '@paypalcorp/pp-react'
import { isEmpty } from 'lodash-es'
import {
  getCurrencyFormat,
  getLocalizedText,
} from 'ppbl/utils/locale-helper.js'
import { interpolate } from 'ppbl/utils/string-helper'
import React, { useContext } from 'react'
import { toCommaSeparatedNumber } from '../__utils__'
import { OfferContext } from './offerContext'

type MetricProps = {
  infoLabel: string
  value?: string
  valueStrong?: boolean
  labelStrong?: boolean
  toolTipText?: string
}

const OfferInfoMetrics: React.FC<MetricProps> = ({
  infoLabel,
  value,
  labelStrong,
  valueStrong,
  toolTipText,
}) => {
  return (
    <div className="infoMetricsContainer">
      <Row id={infoLabel}>
        <Col className="cardColumnLeft" xs={8} sm={8}>
          {labelStrong ? (
            <BodyText strong>{infoLabel}</BodyText>
          ) : (
            <BodyText>{infoLabel}</BodyText>
          )}
          {toolTipText && <Tooltip description={toolTipText} />}
        </Col>
        <Col className="cardColumnRight" xs={4} sm={4}>
          {value && (
            <div>
              {valueStrong ? (
                <HeadingText size="sm">{value}</HeadingText>
              ) : (
                <BodyText>{value}</BodyText>
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}

type SegmentProps = {
  label: string
  value?: string
  strongValue?: boolean
  note?: string
}

const OfferInfoSegment: React.FC<SegmentProps> = ({
  label,
  value,
  strongValue,
  note,
}) => {
  return (
    <div className="infoSegmentContainer">
      <BodyText>{label}</BodyText>
      {value && (
        <div
          className={`${
            strongValue ? 'infoSegmentTextStrong' : 'infoSegmentText'
          }`}
        >
          {strongValue ? (
            <HeadingText size="lg">{value}</HeadingText>
          ) : (
            <BodyText>{value}</BodyText>
          )}
        </div>
      )}
      {note && (
        <div className="infoSegmentText">
          <CaptionText className="captionText">{note}</CaptionText>
        </div>
      )}
    </div>
  )
}

/**
 * This is a functional component that display list of offer metrics
 * @returns {JSX.Element} - A JSX element composed of offer metrics component
 */
const OfferInfo: React.FC = () => {
  const {
    offerValues,
    loanTerm,
    renewalStatus,
    previousLoans,
    previousLoanOutstandingAmount,
  } = useContext(OfferContext)

  return (
    <>
      <div className="cardContainer">
        <Card className="cardWrapper" data-dd-privacy="mask">
          <div className="cardHeading">
            <OfferInfoMetrics
              infoLabel={getLocalizedText('offer-page.offerInfo.headerText')}
              labelStrong
            />
          </div>
          <Divider className="divider" />
          <div className="weekPaymentSection center">
            <OfferInfoSegment
              label={interpolate(
                getLocalizedText('offer-page.offerInfo.infoSegmentHeaderText'),
                {
                  loanTerm,
                },
              )}
              value={getCurrencyFormat(offerValues.weeklyPayment)}
              strongValue
            />
          </div>
          <Divider className="divider" />
          <div className="loanInfoSection">
            <OfferInfoMetrics
              infoLabel={getLocalizedText(
                'offer-page.offerInfo.infoMetricLabel1',
              )}
              value={
                offerValues.offerAmount &&
                getCurrencyFormat(offerValues.offerAmount)
              }
              valueStrong
            />
            <Divider className="divider" />
            <OfferInfoMetrics
              infoLabel={getLocalizedText(
                'offer-page.offerInfo.infoMetricLabel2',
              )}
              value={
                offerValues.financeCharges &&
                '+' + getCurrencyFormat(offerValues.financeCharges)
              }
              toolTipText={getLocalizedText(
                'offer-page.offerInfo.infoMetricTooltip',
              )} //FIXME: - Change the text once FIGMA is updated
            />
            <Divider className="divider" />
            <OfferInfoMetrics
              infoLabel={getLocalizedText(
                'offer-page.offerInfo.infoMetricLabel3',
              )}
              value={
                offerValues?.offerAmount &&
                offerValues?.financeCharges &&
                getCurrencyFormat(
                  offerValues.offerAmount + offerValues.financeCharges,
                )
              }
            />
          </div>
        </Card>
      </div>
      {renewalStatus === 'LOAN_RENEWAL' && !isEmpty(previousLoans) && (
        <div className="cardContainer">
          <Card className="cardWrapper" data-dd-privacy="mask">
            <div className="cardHeading">
              <OfferInfoMetrics
                infoLabel={getLocalizedText(
                  'offer-page.offerInfo.headerTextRenewal',
                )}
                labelStrong
              />
            </div>
            <Divider className="divider" />
            <div className="transactionSummarySection">
              <div className="transactionSummarySegment">
                <OfferInfoSegment
                  label={getLocalizedText(
                    'offer-page.offerInfo.infoMetricLabel1Renewal',
                  )}
                  value={
                    offerValues.offerAmount &&
                    `${getCurrencyFormat(offerValues.offerAmount)}`
                  }
                />
              </div>
              <Divider className="mobileVisible divider" />
              <div className="transactionSummarySegment">
                <OfferInfoSegment
                  label={getLocalizedText(
                    'offer-page.offerInfo.infoMetricLabel2Renewal',
                  )}
                  value={
                    previousLoanOutstandingAmount &&
                    `$${toCommaSeparatedNumber(previousLoanOutstandingAmount)}`
                  }
                  note={interpolate(
                    getLocalizedText(
                      'offer-page.offerInfo.infoMetricNoteRenewal',
                    ),
                    {
                      loanAccountNumbers: (
                        <span>
                          {previousLoans
                            .map((each) => {
                              return `#${each.id}`
                            })
                            .join(' ')}
                        </span>
                      ),
                    },
                  )}
                />
              </div>
              <Divider className="mobileVisible divider" />
              <div className="transactionSummarySegment">
                <OfferInfoSegment
                  label={getLocalizedText(
                    'offer-page.offerInfo.infoMetricLabel3Renewal',
                  )}
                  value={
                    offerValues.offerAmount &&
                    previousLoanOutstandingAmount &&
                    `$${toCommaSeparatedNumber(
                      offerValues.offerAmount - previousLoanOutstandingAmount,
                    )}`
                  }
                  strongValue
                />
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  )
}

export default OfferInfo
